import React from 'react';
import { MessageSquare, Copy, CheckCircle, AlertTriangle } from 'lucide-react';
import ModernDocHeader from '../../../components/documentation/ModernDocHeader';

const WhatsAppIntegrationDocs: React.FC = () => {
  const webhookUrl = `${window.location.origin}/api/webhooks/whatsapp`;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div>
      <ModernDocHeader
        title="WhatsApp Integration"
        description="Capture leads from WhatsApp Business conversations automatically. We support WATI and AiSensy platforms for seamless lead generation."
        icon={<MessageSquare className="w-6 h-6" />}
        badge="Popular"
        badgeColor="green"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* Supported Providers */}
      <h2>Supported Platforms</h2>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">WATI (WhatsApp Team Inbox)</h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            Official WhatsApp Business API provider with team collaboration features
          </p>
          <a href="https://www.wati.io" target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline mt-2 inline-block">
            Visit WATI →
          </a>
        </div>
        <div className="p-4 border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">AiSensy</h4>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            WhatsApp automation platform with chatbots and broadcasts
          </p>
          <a href="https://www.aisensy.com" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
            Visit AiSensy →
          </a>
        </div>
      </div>

      {/* What Gets Captured */}
      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              What Gets Captured Automatically
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Customer Name</strong> - WhatsApp profile name</li>
              <li>✓ <strong>Phone Number</strong> - Customer's WhatsApp number</li>
              <li>✓ <strong>Message Content</strong> - First message from customer</li>
              <li>✓ <strong>Timestamp</strong> - When message was received</li>
              <li>✓ <strong>Conversation Context</strong> - Complete message thread</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Setup Guide - WATI</h2>

      <h3>Step 1: Configure WATI Integration</h3>
      <ol>
        <li>Log into your WATI dashboard</li>
        <li>Go to <code>Settings → Integrations → Webhooks</code></li>
        <li>Click "Add New Webhook"</li>
      </ol>

      <h3>Step 2: Add CRM Webhook URL</h3>
      <div className="not-prose my-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">WATI Webhook URL</p>
            <button
              onClick={() => copyToClipboard(webhookUrl)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}
          </code>
        </div>
      </div>

      <h3>Step 3: Configure Webhook Events</h3>
      <p>Enable the following events in WATI:</p>
      <ul>
        <li><strong>message.received</strong> - When customer sends first message</li>
        <li><strong>conversation.started</strong> - When new conversation begins</li>
      </ul>

      <h3>Step 4: Set Authentication (if required)</h3>
      <p>Some platforms require authentication. Contact support for API keys if needed.</p>

      <h2>Setup Guide - AiSensy</h2>

      <h3>Step 1: Access AiSensy Dashboard</h3>
      <ol>
        <li>Log into your AiSensy account</li>
        <li>Navigate to <code>Settings → API & Webhooks</code></li>
        <li>Find the "Webhook Configuration" section</li>
      </ol>

      <h3>Step 2: Add Webhook Endpoint</h3>
      <div className="not-prose my-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">AiSensy Webhook URL</p>
            <button
              onClick={() => copyToClipboard(webhookUrl)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}
          </code>
        </div>
      </div>

      <h3>Step 3: Enable Lead Capture Events</h3>
      <p>Select these events to trigger lead creation:</p>
      <ul>
        <li>New Message</li>
        <li>Conversation Started</li>
        <li>User Details Captured</li>
      </ul>

      <h2>How It Works</h2>

      <h3>Message Flow</h3>
      <ol>
        <li><strong>Customer Messages</strong> - Customer sends WhatsApp message to your business number</li>
        <li><strong>Platform Processes</strong> - WATI/AiSensy receives and processes the message</li>
        <li><strong>Webhook Triggered</strong> - Platform sends message data to your CRM</li>
        <li><strong>CRM Processes</strong>:
          <ul>
            <li>Extracts customer phone number and name</li>
            <li>Checks for existing lead</li>
            <li>Creates new lead or updates existing one</li>
            <li>Saves message content as lead remarks</li>
            <li>Assigns to team member (if Round Robin configured)</li>
          </ul>
        </li>
        <li><strong>Lead Available</strong> - Lead appears in dashboard with WhatsApp as source</li>
      </ol>

      <h3>Duplicate Handling</h3>
      <p>
        WhatsApp leads are matched by phone number:
      </p>
      <ul>
        <li><strong>First Contact:</strong> Creates new lead with WhatsApp source</li>
        <li><strong>Follow-up Messages:</strong> Added as remarks to existing lead</li>
        <li><strong>Conversation History:</strong> All messages tracked in lead timeline</li>
      </ul>

      <h2>Lead Data Mapping</h2>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">WhatsApp Field</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">CRM Field</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Phone Number</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>phone</code></td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Profile Name</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>name</code></td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Message Text</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>message</code></td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Timestamp</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>created_at</code></td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Platform</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>source = "whatsapp"</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>Testing Your Integration</h2>

      <h3>Test Checklist</h3>
      <ol>
        <li>Send a test WhatsApp message to your business number</li>
        <li>Check CRM dashboard - new lead should appear within seconds</li>
        <li>Verify lead data:
          <ul>
            <li>Phone number is correct</li>
            <li>Name is captured (if available)</li>
            <li>Message content is in remarks</li>
            <li>Source shows "WhatsApp"</li>
          </ul>
        </li>
        <li>Send a follow-up message - should update existing lead, not create duplicate</li>
      </ol>

      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-6">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Use WATI/AiSensy's "Test Webhook" feature to simulate messages without actually sending WhatsApp messages.
        </p>
      </div>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Messages not creating leads?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Check:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Webhook URL is correctly configured in WATI/AiSensy</li>
              <li>Webhook events are enabled</li>
              <li>Your WhatsApp number is verified with the platform</li>
              <li>Test webhook from platform's dashboard</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Duplicate leads being created?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>The system matches leads by phone number. If duplicates appear:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Check if phone numbers have different formats (with/without country code)</li>
              <li>Verify webhook is not configured multiple times</li>
              <li>Contact support if issue persists</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Missing customer name?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>WhatsApp may not always provide the customer's name. This is normal and depends on:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Customer's privacy settings</li>
              <li>Whether customer has saved name in profile</li>
              <li>Platform's ability to extract name</li>
            </ul>
            <p className="mt-2">The phone number will always be captured, which is the primary identifier.</p>
          </div>
        </details>
      </div>

      <h2>Advanced Features</h2>

      <h3>Auto-Reply Integration</h3>
      <p>
        While leads are captured in your CRM, you can set up auto-replies in WATI/AiSensy:
      </p>
      <ul>
        <li>Welcome message when customer first contacts</li>
        <li>Business hours notification</li>
        <li>FAQ responses for common questions</li>
      </ul>

      <h3>Broadcast Messages</h3>
      <p>
        Use your WhatsApp platform to send broadcast messages to leads:
      </p>
      <ul>
        <li>Export lead phone numbers from CRM</li>
        <li>Create broadcast list in WATI/AiSensy</li>
        <li>Send promotional or follow-up messages</li>
      </ul>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
              WhatsApp Guidelines
            </h4>
            <ul className="space-y-2 text-sm text-yellow-800 dark:text-yellow-300">
              <li>• Only message customers who have initiated contact first</li>
              <li>• Follow WhatsApp's 24-hour messaging window policy</li>
              <li>• Use approved message templates for marketing</li>
              <li>• Respect opt-out requests immediately</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Integration Complete!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              Your WhatsApp integration is ready. All messages will automatically create leads in your CRM.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="/settings/wati"
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                WATI Settings
              </a>
              <a
                href="/settings/aisensy"
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                AiSensy Settings
              </a>
            </div>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppIntegrationDocs;
